<script setup lang="ts">
  import type { PropType } from 'vue'

  import { computed } from 'vue'

  import { Lock01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils/core.utils'

  import { useLocation } from '@common/location-layer/composables/useLocation'

  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

  import CPText from '@ui/components/CPText.vue'

  import { useLearnerApi } from '@courses/learner-api-layer/composables/useLearnerApi'

  import { getPostContentTypeIcon } from '@courses/utility-layer/content-type-icon'
  import type { LessonResult } from '@courses/types-layer/types/search'

  const props = defineProps({
    lesson: {
      type: Object as PropType<LessonResult>,
      required: true,
    },
  })

  const { CPNavigateTo } = useCPNavigation()
  const { locationId } = useLocation()
  const { getUnlockedModules } = useLearnerApi(() => props.lesson.productId, locationId)
  const unlockedModulesQuery = getUnlockedModules()

  const isDripLocked = computed(() => {
    const categoryId = props.lesson.categoryId
    if (!categoryId) return false
    if (unlockedModulesQuery.isLoading.value || unlockedModulesQuery.isPending.value) return true
    if (unlockedModulesQuery.error.value || !unlockedModulesQuery.data.value) return true
    return unlockedModulesQuery.data.value?.dripCategoryUnlockedMap?.[categoryId] === false
  })

  const contentIcon = computed(() => (isDripLocked.value ? Lock01Icon : getPostContentTypeIcon(props.lesson.contentType)))

  const handleClick = async () => {
    if (isDripLocked.value) return

    if (props.lesson.categoryId) {
      await CPNavigateTo(
        {
          name: 'courses-products-modules-lessons',
          params: {
            productId: props.lesson.productId,
            moduleId: props.lesson.categoryId,
            lessonId: props.lesson._id,
          },
        },
        true
      )
      return
    }

    await CPNavigateTo(
      {
        name: 'courses-products',
        params: {
          productId: props.lesson.productId,
        },
      },
      true
    )
  }
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- locked rows keep a no-op click while role/tabindex are removed -->
  <div
    :id="createElementId('search-result', 'lesson-card', lesson._id)"
    class="lesson-card"
    :class="{ 'lesson-card--locked': isDripLocked }"
    :role="isDripLocked ? undefined : 'button'"
    :tabindex="isDripLocked ? undefined : 0"
    @click="handleClick"
    @keydown.enter="handleClick"
  >
    <div class="h-[1.75rem] w-[3.125rem] flex-shrink-0 overflow-hidden rounded-lg bg-hover">
      <OptimisedImage
        v-if="lesson.thumbnail"
        :src="lesson.thumbnail"
        :alt="lesson.title"
        source="courses"
        gradient-mode="always"
        fallback-aspect="aspect-video"
        loading="eager"
        fetchpriority="high"
      />
      <div
        v-else
        :id="createElementId('search-result', 'lesson-card-fallback', lesson._id)"
        class="h-full w-full bg-gradient-to-br from-[var(--cp-primary-200)] to-[var(--cp-primary-50)]"
      />
    </div>

    <div class="ml-2 min-w-0 flex-1">
      <CPText size="lg" weight="semibold" class="line-clamp-1 text-body">
        {{ lesson.title }}
      </CPText>
    </div>

    <div class="ml-2 flex-shrink-0">
      <component :is="contentIcon" class="h-[0.875rem] w-[0.875rem] text-muted" />
    </div>
  </div>
</template>

<style scoped>
  .lesson-card {
    display: flex;
    align-items: center;
    padding: 12px;
    cursor: pointer;
    border: 1px solid var(--cp-border-500);
    border-radius: 8px;
  }

  .lesson-card--locked {
    cursor: default;
  }
</style>
